import type { WidgetFieldSource } from "@/domains/buildings/types/buildingWidget";
import { canonicalAttributeValue, sameAttributeValue } from "@/utils/attributeValue";

type MachineSource = Exclude<WidgetFieldSource, { kind: "user" }>;

/**
 * One document backing a value, with the rationale it reported.
 *
 * No page: a finding row carries a single `reasoning` string that the widget
 * mapper copies onto every one of its `source_filenames`, so the page scraped
 * out of it was stamped identically on every file listed here and was right
 * for at most one of them (a .dwg row carrying "Page 30" gave it away). The
 * page comes back when the extractor persists per-document evidence.
 */
export interface EvidenceDoc {
  filename: string;
  reasoning: string | null;
}

/** One registry backing a value. `sourceSystem` decides chip vs plain name. */
export interface EvidenceRegistry {
  sourceSystem: string;
  reasoning: string | null;
}

/** Every source that reported one value, so the section can group by answer. */
export interface EvidenceValueGroup {
  value: string;
  docs: EvidenceDoc[];
  registries: EvidenceRegistry[];
}

/** A registry repeating itself is one row; keep the first stated reason. */
function addRegistry(group: EvidenceValueGroup, source: Extract<MachineSource, { kind: "ehr" }>) {
  const seen = group.registries.find((entry) => entry.sourceSystem === source.sourceSystem);
  if (!seen) {
    group.registries.push({ sourceSystem: source.sourceSystem, reasoning: source.reasoning });
    return;
  }
  if (!seen.reasoning && source.reasoning) seen.reasoning = source.reasoning;
}

/** One row per file, however many pipelines read it; first reason wins. */
function addDoc(group: EvidenceValueGroup, source: Exclude<MachineSource, { kind: "ehr" }>) {
  const seen = group.docs.find((doc) => doc.filename === source.filename);
  if (!seen) {
    group.docs.push({
      filename: source.filename,
      reasoning: source.reasoning,
    });
    return;
  }
  if (!seen.reasoning && source.reasoning) seen.reasoning = source.reasoning;
}

/**
 * Groups a field's machine sources by the value they reported, compared as
 * `canonicalAttributeValue` compares them.
 *
 * Distinct from `groupNonUserSources`, which keys on (kind, value) and keeps
 * only the first reasoning of a group: the evidence section shows a ⓘ per
 * document, so the per-file `reasoning` has to survive.
 * Extractor and IFC findings merge here — the reader cares which documents back
 * a value, not which pipeline read them — while two registries reporting the
 * same value stay separate rows, since only Ehitisregister earns its deep link.
 *
 * Insertion-ordered: the first value a source reports leads, matching the order
 * `matchingMachineSources` resolves the primary source in.
 */
export function evidenceValueGroups(sources: MachineSource[]): EvidenceValueGroup[] {
  const byValue = new Map<string, EvidenceValueGroup>();
  for (const source of sources) {
    const key = canonicalAttributeValue(source.value);
    let group = byValue.get(key);
    if (!group) {
      group = { value: source.value, docs: [], registries: [] };
      byValue.set(key, group);
    }
    if (source.kind === "ehr") addRegistry(group, source);
    else addDoc(group, source);
  }
  return Array.from(byValue.values());
}

/** Rows a group will render — a document row per file, a row per registry. */
export function evidenceGroupRowCount(group: EvidenceValueGroup): number {
  return group.docs.length + group.registries.length;
}

/**
 * The same comparison `matchingMachineSources` uses, so a stray space or a
 * trailing ".0" cannot orphan the group holding the field's own value.
 */
export function isSameEvidenceValue(a: string, b: string): boolean {
  return sameAttributeValue(a, b);
}
